import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, Check, MousePointer2, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface PreviewAndCropProps {
  screenStream: MediaStream;
  onSelectArea: (area: { x: number; y: number; width: number; height: number }) => void;
  onCancel: () => void;
  /** Previously saved area in stream coordinates — restored when the component mounts. */
  initialArea?: { x: number; y: number; width: number; height: number };
}

const PreviewAndCrop: React.FC<PreviewAndCropProps> = ({ screenStream, onSelectArea, onCancel, initialArea }) => {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  
  const [isSelecting, setIsSelecting] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [currentPos, setCurrentPos] = useState({ x: 0, y: 0 });
  const [selectedArea, setSelectedArea] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState<string | null>(null);
  
  // Original stream dimensions
  const [streamDimensions, setStreamDimensions] = useState({ width: 0, height: 0 });
  // Preview dimensions (may be scaled to fit the screen)
  const [previewDimensions, setPreviewDimensions] = useState({ width: 0, height: 0 });
  
  // Calculate the selection rectangle dimensions
  const selectionRect = selectedArea || {
    x: Math.min(startPos.x, currentPos.x),
    y: Math.min(startPos.y, currentPos.y),
    width: Math.abs(currentPos.x - startPos.x),
    height: Math.abs(currentPos.y - startPos.y)
  };

  // Set up video element with the screen stream
  useEffect(() => {
    if (videoRef.current && screenStream) {
      videoRef.current.srcObject = screenStream;
      
      // When video metadata is loaded, we can get the actual dimensions
      videoRef.current.onloadedmetadata = () => {
        if (videoRef.current) {
          const videoWidth = videoRef.current.videoWidth;
          const videoHeight = videoRef.current.videoHeight;
          
          console.log('[PreviewAndCrop] Stream dimensions:', { width: videoWidth, height: videoHeight });
          setStreamDimensions({ width: videoWidth, height: videoHeight });
          
          // Use less height so instructions + buttons fit without scrolling
          const containerWidth = window.innerWidth * 0.88;
          const containerHeight = window.innerHeight * 0.62;
          
          let previewWidth, previewHeight;
          
          if (videoWidth / videoHeight > containerWidth / containerHeight) {
            previewWidth = containerWidth;
            previewHeight = containerWidth * (videoHeight / videoWidth);
          } else {
            previewHeight = containerHeight;
            previewWidth = containerHeight * (videoWidth / videoHeight);
          }
          
          console.log('[PreviewAndCrop] Preview dimensions:', { width: previewWidth, height: previewHeight });
          setPreviewDimensions({ width: previewWidth, height: previewHeight });
          
          videoRef.current.width = previewWidth;
          videoRef.current.height = previewHeight;

          // Restore the previous selection if available; otherwise default to central 60%.
          if (initialArea && videoWidth > 0 && videoHeight > 0) {
            // Convert from stream coordinates to preview coordinates.
            const scaleX = previewWidth / videoWidth;
            const scaleY = previewHeight / videoHeight;
            const restored = {
              x: Math.round(initialArea.x * scaleX),
              y: Math.round(initialArea.y * scaleY),
              width: Math.round(initialArea.width * scaleX),
              height: Math.round(initialArea.height * scaleY),
            };
            // Clamp to preview bounds to avoid out-of-bounds selections.
            restored.x = Math.max(0, Math.min(restored.x, previewWidth - restored.width));
            restored.y = Math.max(0, Math.min(restored.y, previewHeight - restored.height));
            restored.width = Math.min(restored.width, previewWidth - restored.x);
            restored.height = Math.min(restored.height, previewHeight - restored.y);
            if (restored.width >= 100 && restored.height >= 100) {
              setSelectedArea(restored);
            } else {
              // Fallback to central 60% if restored area is too small.
              const margin = 0.2;
              setSelectedArea({
                x: Math.round(previewWidth * margin),
                y: Math.round(previewHeight * margin),
                width: Math.round(previewWidth * (1 - margin * 2)),
                height: Math.round(previewHeight * (1 - margin * 2)),
              });
            }
          } else {
            // No saved area — use central 60% as default.
            const margin = 0.2;
            setSelectedArea({
              x: Math.round(previewWidth * margin),
              y: Math.round(previewHeight * margin),
              width: Math.round(previewWidth * (1 - margin * 2)),
              height: Math.round(previewHeight * (1 - margin * 2)),
            });
          }
        }
      };
      
      videoRef.current.play().catch(err => {
        console.error('[PreviewAndCrop] Error playing video:', err);
      });
    }
    
    return () => {
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    };
  }, [screenStream]);

  /** Primary pointer id for drag/resize (touch + mouse unified). */
  const activePointerIdRef = useRef<number | null>(null);

  const getOverlayCoords = useCallback((clientX: number, clientY: number) => {
    if (!overlayRef.current) return { x: 0, y: 0 };
    const rect = overlayRef.current.getBoundingClientRect();
    return { x: clientX - rect.left, y: clientY - rect.top };
  }, []);

  // Pointer down — works for mouse, touch, and pen (mouse-only handlers miss touch on mobile)
  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!e.isPrimary) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if (!overlayRef.current) return;

    const { x, y } = getOverlayCoords(e.clientX, e.clientY);

    console.log('[PreviewAndCrop] Pointer down at:', { x, y, pointerType: e.pointerType });

    activePointerIdRef.current = e.pointerId;
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* ignore — e.g. pointer already captured */
    }

    const target = e.target as HTMLElement;
    const isControlElement = target.closest('button') || target.closest('svg') || target.closest('path');

    if (isControlElement) {
      console.log('[PreviewAndCrop] Hit control element, ignoring');
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      activePointerIdRef.current = null;
      return;
    }

    if (selectedArea) {
      // Check if clicking inside the selected area (for dragging)
      const { x: areaX, y: areaY, width, height } = selectedArea;
      
      // Check if clicking on resize handles
      const handleSize = 8;
      const isOnTopLeft = Math.abs(x - areaX) <= handleSize && Math.abs(y - areaY) <= handleSize;
      const isOnTopRight = Math.abs(x - (areaX + width)) <= handleSize && Math.abs(y - areaY) <= handleSize;
      const isOnBottomLeft = Math.abs(x - areaX) <= handleSize && Math.abs(y - (areaY + height)) <= handleSize;
      const isOnBottomRight = Math.abs(x - (areaX + width)) <= handleSize && Math.abs(y - (areaY + height)) <= handleSize;
      const isOnTop = !isOnTopLeft && !isOnTopRight && Math.abs(y - areaY) <= handleSize && x > areaX && x < areaX + width;
      const isOnBottom = !isOnBottomLeft && !isOnBottomRight && Math.abs(y - (areaY + height)) <= handleSize && x > areaX && x < areaX + width;
      const isOnLeft = !isOnTopLeft && !isOnBottomLeft && Math.abs(x - areaX) <= handleSize && y > areaY && y < areaY + height;
      const isOnRight = !isOnTopRight && !isOnBottomRight && Math.abs(x - (areaX + width)) <= handleSize && y > areaY && y < areaY + height;
      
      if (isOnTopLeft || isOnTopRight || isOnBottomLeft || isOnBottomRight || isOnTop || isOnBottom || isOnLeft || isOnRight) {
        console.log('[PreviewAndCrop] Starting resize operation');
        setIsResizing(true);
        if (isOnTopLeft) setResizeHandle('topLeft');
        else if (isOnTopRight) setResizeHandle('topRight');
        else if (isOnBottomLeft) setResizeHandle('bottomLeft');
        else if (isOnBottomRight) setResizeHandle('bottomRight');
        else if (isOnTop) setResizeHandle('top');
        else if (isOnBottom) setResizeHandle('bottom');
        else if (isOnLeft) setResizeHandle('left');
        else if (isOnRight) setResizeHandle('right');
        return;
      }
      
      if (x >= areaX && x <= areaX + width && y >= areaY && y <= areaY + height) {
        console.log('[PreviewAndCrop] Starting drag operation');
        setIsDragging(true);
        setDragOffset({
          x: x - areaX,
          y: y - areaY
        });
        return;
      }
    }
    
    // Start a new selection
    console.log('[PreviewAndCrop] Starting new selection');
    setIsSelecting(true);
    setStartPos({ x, y });
    setCurrentPos({ x, y });
    setSelectedArea(null);
  }, [selectedArea, getOverlayCoords]);

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (activePointerIdRef.current !== null && e.pointerId !== activePointerIdRef.current) return;
      if (!overlayRef.current) return;

      const { x, y } = getOverlayCoords(e.clientX, e.clientY);

      if (isSelecting) {
        setCurrentPos({ x, y });
      } else if (isDragging && selectedArea) {
        const newX = x - dragOffset.x;
        const newY = y - dragOffset.y;

        const maxX = previewDimensions.width - selectedArea.width;
        const maxY = previewDimensions.height - selectedArea.height;

        const updatedArea = {
          ...selectedArea,
          x: Math.max(0, Math.min(newX, maxX)),
          y: Math.max(0, Math.min(newY, maxY)),
        };

        setSelectedArea(updatedArea);
      } else if (isResizing && selectedArea && resizeHandle) {
        let newArea = { ...selectedArea };

        switch (resizeHandle) {
          case 'topLeft':
            newArea = {
              x: Math.min(x, selectedArea.x + selectedArea.width),
              y: Math.min(y, selectedArea.y + selectedArea.height),
              width: Math.abs(selectedArea.x + selectedArea.width - x),
              height: Math.abs(selectedArea.y + selectedArea.height - y),
            };
            break;
          case 'topRight':
            newArea = {
              x: selectedArea.x,
              y: Math.min(y, selectedArea.y + selectedArea.height),
              width: Math.max(0, x - selectedArea.x),
              height: Math.abs(selectedArea.y + selectedArea.height - y),
            };
            break;
          case 'bottomLeft':
            newArea = {
              x: Math.min(x, selectedArea.x + selectedArea.width),
              y: selectedArea.y,
              width: Math.abs(selectedArea.x + selectedArea.width - x),
              height: Math.max(0, y - selectedArea.y),
            };
            break;
          case 'bottomRight':
            newArea = {
              x: selectedArea.x,
              y: selectedArea.y,
              width: Math.max(0, x - selectedArea.x),
              height: Math.max(0, y - selectedArea.y),
            };
            break;
          case 'top':
            newArea = {
              ...selectedArea,
              y: Math.min(y, selectedArea.y + selectedArea.height),
              height: Math.abs(selectedArea.y + selectedArea.height - y),
            };
            break;
          case 'bottom':
            newArea = {
              ...selectedArea,
              height: Math.max(0, y - selectedArea.y),
            };
            break;
          case 'left':
            newArea = {
              x: Math.min(x, selectedArea.x + selectedArea.width),
              y: selectedArea.y,
              width: Math.abs(selectedArea.x + selectedArea.width - x),
              height: selectedArea.height,
            };
            break;
          case 'right':
            newArea = {
              ...selectedArea,
              width: Math.max(0, x - selectedArea.x),
            };
            break;
        }

        if (newArea.width >= 100 && newArea.height >= 100) {
          setSelectedArea(newArea);
        }
      }
    },
    [
      isSelecting,
      isDragging,
      isResizing,
      selectedArea,
      dragOffset,
      resizeHandle,
      previewDimensions.width,
      previewDimensions.height,
      getOverlayCoords,
    ],
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (activePointerIdRef.current !== null && e.pointerId !== activePointerIdRef.current) return;

      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      activePointerIdRef.current = null;

      if (isSelecting) {
        setIsSelecting(false);

        const x = Math.min(startPos.x, currentPos.x);
        const y = Math.min(startPos.y, currentPos.y);
        const width = Math.abs(currentPos.x - startPos.x);
        const height = Math.abs(currentPos.y - startPos.y);

        if (width >= 100 && height >= 100) {
          setSelectedArea({ x, y, width, height });
        }
      }

      setIsDragging(false);
      setIsResizing(false);
      setResizeHandle(null);
    },
    [isSelecting, startPos, currentPos],
  );

  // Handle key press to cancel selection with Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancel();
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onCancel]);

  // Set cursor style based on current action
  useEffect(() => {
    if (isSelecting) {
      document.body.style.cursor = 'crosshair';
    } else if (isDragging) {
      document.body.style.cursor = 'move';
    } else if (isResizing) {
      switch (resizeHandle) {
        case 'topLeft':
        case 'bottomRight':
          document.body.style.cursor = 'nwse-resize';
          break;
        case 'topRight':
        case 'bottomLeft':
          document.body.style.cursor = 'nesw-resize';
          break;
        case 'top':
        case 'bottom':
          document.body.style.cursor = 'ns-resize';
          break;
        case 'left':
        case 'right':
          document.body.style.cursor = 'ew-resize';
          break;
      }
    } else {
      document.body.style.cursor = 'crosshair';
    }
    
    return () => {
      document.body.style.cursor = 'default';
    };
  }, [isSelecting, isDragging, isResizing, resizeHandle]);

  // Handle confirm button click
  const handleConfirm = () => {
    if (!selectedArea) return;

    if (
      streamDimensions.width <= 0 ||
      streamDimensions.height <= 0 ||
      previewDimensions.width <= 0 ||
      previewDimensions.height <= 0
    ) {
      // Overlay pixels are not yet mapped to the capture; do not start with preview coords
      // (useRecording treats selectedArea as stream pixels).
      console.warn('[PreviewAndCrop] stream/preview dimensions not ready; confirm ignored');
      return;
    }

    const scaleX = streamDimensions.width / previewDimensions.width;
    const scaleY = streamDimensions.height / previewDimensions.height;
    const scaledArea = {
      x: Math.round(selectedArea.x * scaleX),
      y: Math.round(selectedArea.y * scaleY),
      width: Math.round(selectedArea.width * scaleX),
      height: Math.round(selectedArea.height * scaleY),
    };
    console.log('[PreviewAndCrop] Scaled area (stream coords):', scaledArea);
    console.log('[PreviewAndCrop] Original selected area:', selectedArea);
    onSelectArea(scaledArea);
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-90 flex flex-col items-center justify-center z-50"
      style={{ zIndex: 2000, overflowY: 'auto', padding: '16px 0' }}
    >
      {/* Title + instructions */}
      <div className="text-white text-center mb-3 px-4" style={{ maxWidth: '640px' }}>
        <h2 className="text-xl font-bold mb-1">{t('select_area')}</h2>
        <p className="text-sm opacity-80 flex items-center justify-center gap-1">
          <MousePointer2 className="w-4 h-4 inline-block" aria-hidden />
          {t('draw_selection_on_preview', 'Click and drag on the preview to select the area to record')}
        </p>
      </div>

      {/* Mirror-loop warning — visible when the user shared the whole screen */}
      <div
        className="flex items-start gap-2 rounded-md px-3 py-2 mb-3 text-xs"
        style={{
          background: 'rgba(255,180,0,0.15)',
          border: '1px solid rgba(255,180,0,0.4)',
          color: '#ffd166',
          maxWidth: '640px',
          width: '88vw',
        }}
      >
        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" aria-hidden />
        <span>
          {t(
            'mirror_loop_warning',
            'If you see an infinite mirror effect, stop sharing and choose "Window" or a specific tab instead of "Entire screen".',
          )}
        </span>
      </div>
      
      <div className="relative" style={{ width: previewDimensions.width, height: previewDimensions.height }}>
        {/* Video preview */}
        <video 
          ref={videoRef}
          className="bg-black"
          muted
          playsInline
          style={{ display: 'block', width: '100%', height: '100%', objectFit: 'fill' }}
        />
        
        {/* Selection overlay */}
        <div 
          ref={overlayRef}
          className="absolute inset-0 select-none"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          style={{ cursor: 'crosshair', touchAction: 'none' }}
        >
          {/* Selection area */}
          {(isSelecting || selectedArea) && (
            <div
              className="absolute border-2 border-blue-500 bg-transparent"
              style={{
                left: `${selectionRect.x}px`,
                top: `${selectionRect.y}px`,
                width: `${selectionRect.width}px`,
                height: `${selectionRect.height}px`,
                boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)',
                cursor: isDragging ? 'move' : 'default'
              }}
            >
              {/* Resize handles - only show when area is selected */}
              {selectedArea && (
                <>
                  <div className="absolute -top-1 -left-1 w-3 h-3 bg-blue-500 rounded-full cursor-nwse-resize" />
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 rounded-full cursor-nesw-resize" />
                  <div className="absolute -bottom-1 -left-1 w-3 h-3 bg-blue-500 rounded-full cursor-nesw-resize" />
                  <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-blue-500 rounded-full cursor-nwse-resize" />
                  <div className="absolute top-1/2 -left-1 w-3 h-3 bg-blue-500 rounded-full cursor-ew-resize transform -translate-y-1/2" />
                  <div className="absolute top-1/2 -right-1 w-3 h-3 bg-blue-500 rounded-full cursor-ew-resize transform -translate-y-1/2" />
                  <div className="absolute -top-1 left-1/2 w-3 h-3 bg-blue-500 rounded-full cursor-ns-resize transform -translate-x-1/2" />
                  <div className="absolute -bottom-1 left-1/2 w-3 h-3 bg-blue-500 rounded-full cursor-ns-resize transform -translate-x-1/2" />
                </>
              )}
            </div>
          )}

          {/* Dimensions display */}
          {(isSelecting || selectedArea) && (
            <div 
              className="absolute bg-black text-white text-xs px-2 py-1 rounded pointer-events-none"
              style={{
                left: `${selectionRect.x + selectionRect.width / 2}px`,
                top: `${selectionRect.y + selectionRect.height + 8}px`,
                transform: 'translateX(-50%)'
              }}
            >
              {Math.round(selectionRect.width)} × {Math.round(selectionRect.height)}
            </div>
          )}
        </div>
      </div>
      
      {/* Control buttons */}
      <div className="mt-4 flex flex-col items-center gap-2">
        <div className="flex gap-4">
          <button
            onClick={onCancel}
            className="px-4 py-2 rounded-md flex items-center gap-2 text-white"
            style={{
              backgroundColor: 'rgba(255,255,255,0.12)',
              border: '1px solid rgba(255,255,255,0.25)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.12)';
            }}
          >
            <X className="w-4 h-4" />
            {t('cancel')}
          </button>
          
          <button
            onClick={handleConfirm}
            disabled={!selectedArea || streamDimensions.width <= 0}
            className="px-4 py-2 rounded-md flex items-center gap-2 font-medium"
            style={{
              backgroundColor: selectedArea ? '#dc2626' : 'rgba(255,255,255,0.1)',
              color: selectedArea ? '#ffffff' : 'rgba(255,255,255,0.35)',
              cursor: selectedArea ? 'pointer' : 'not-allowed',
              border: '1px solid transparent',
            }}
            onMouseEnter={(e) => {
              if (selectedArea) e.currentTarget.style.backgroundColor = '#b91c1c';
            }}
            onMouseLeave={(e) => {
              if (selectedArea) e.currentTarget.style.backgroundColor = '#dc2626';
            }}
          >
            <Check className="w-4 h-4" />
            {t('confirm_and_record', 'Confirm and Record')}
          </button>
        </div>
        {/* Drag hint when no area is selected yet */}
        {!selectedArea && (
          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>
            {t('drag_to_select_hint', 'Drag on the preview above to select an area first')}
          </p>
        )}
      </div>
    </div>
  );
};

export default PreviewAndCrop;
